<!--
  CollectorFigures — quantity × price = amount as three figures (collector-
  clarity, handoff spec Section 6.1). The single-line card, the grouped card's
  header and the detail sheet say a line's money with it, so the three never
  disagree.

  Give it ONE of:
    line   one line. A collected line shows what his mark stands for (found
           quantity × shelf price); any other line shows the order.
    lines  a grouped card: the pieces, the shared price and what the product
           costs him, over its live lines (collector/moneyView.js `groupMoney`).

  States:
    deposit   the amount cell turns blue and says what he PAYS («تدفع»); the
              full total and the deposit are one line under the row, so the
              card never shows two competing amounts. A line he did not find
              pays nothing and gets no blue cell. `foot: false` leaves the
              line out (a reservation says its deposit in its own block).
    changed   a collected line whose found quantity or shelf price differs:
              that cell is amber, and the quantity reads «2 من 3».
    no price  the price and the amount read «—».
    mixed     a grouped card whose lines do not share one price: «—» under
              «أسعار مختلفة» (each member row then prints its own price).
    long      a number longer than five characters drops to 18 px.

  Drawn with spans so it can sit inside the card's open target (a <button>).
  The row is one `role="group"` with the whole sentence as its label; the
  cells are hidden from a screen reader. Nothing here is floored and nothing is
  summed that collector/moneyView.js does not already sum.
-->
<template>
  <span class="c-figures">
    <span class="c-fig c-num" role="group" :aria-label="sentence">
      <span
        class="c-fig__cell c-fig__cell--qty"
        :class="{ 'c-fig__cell--chg': view.qtyChanged }"
        aria-hidden="true"
      >
        <span class="c-fig__lab">{{ t('collector.figures.qty') }}</span>
        <span class="c-fig__val" :class="{ 'c-fig__val--long': view.qtyChanged }">
          <span class="c-fig__num">{{ view.qtyText }}</span>
          <small class="c-fig__unit">{{ view.qtyWord }}</small>
        </span>
      </span>
      <span class="c-fig__op" aria-hidden="true">×</span>
      <span
        class="c-fig__cell c-fig__cell--price"
        :class="{ 'c-fig__cell--chg': view.priceChanged }"
        aria-hidden="true"
      >
        <span class="c-fig__lab">{{ t(view.priceLabel) }}</span>
        <span class="c-fig__val" :class="{ 'c-fig__val--long': isLong(view.priceText) }">
          <span class="c-fig__num">{{ view.priceText }}</span>
          <small v-if="view.priceText !== DASH" class="c-fig__unit">{{ t('collector.cash.unit') }}</small>
        </span>
      </span>
      <span class="c-fig__op" aria-hidden="true">=</span>
      <span
        class="c-fig__cell c-fig__cell--total"
        :class="{ 'c-fig__cell--pay': view.pays }"
        aria-hidden="true"
      >
        <span class="c-fig__lab">{{ t(view.amountLabel) }}</span>
        <span class="c-fig__val" :class="{ 'c-fig__val--long': isLong(view.amountText) }">
          <span class="c-fig__num">{{ view.amountText }}</span>
          <small v-if="view.amountText !== DASH" class="c-fig__unit">{{ t('collector.cash.unit') }}</small>
        </span>
      </span>
    </span>
    <span v-if="foot && view.footText" class="c-fig__foot c-num">{{ view.footText }}</span>
  </span>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { lineSpend } from '@/collector/dayModel.js';
import { countPhrase, countWord, phraseText } from '@/collector/dayView.js';
import { isLiveLine } from '@/collector/lineView.js';
import { groupMoney, linePaidCents, linePayableCents } from '@/collector/moneyView.js';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  /** One line of the day document. */
  line: { type: Object, default: null },
  /** A grouped card's lines; when given, the figures are the product's. */
  lines: { type: Array, default: null },
  /** Draw the deposit line under the row. */
  foot: { type: Boolean, default: true },
});

const { t } = useI18n();
const { formatAmount } = useCurrency();
// A deposit larger than the total is not floored: the amount goes below zero.
// The sign is U+2212 and stays on the digits (Intl keeps it inside its LRM).
const pounds = (cents) => formatAmount(cents, { trimZeros: true }).replace('-', '−');
const pieces = (qty) => phraseText(countPhrase('pieces', qty), t, pounds);

const DASH = '—';

/** «12,480» is six characters: one more than the cell holds at 21 px. */
const isLong = (text) => text.length > 5;

function lineView(line) {
  const live = isLiveLine(line);
  const collected = live && line?.outcome === 'collected';
  const spend = lineSpend(line);
  const orderedQty = Number(line?.qty) || 0;
  const listUnit = Number(line?.unitPriceCents) || 0;
  const qty = collected ? spend.qty : orderedQty;
  const unit = collected ? spend.price : listUnit;
  const total = collected ? spend.gross : Number(line?.totalCents) || 0;
  const deposit = Number(line?.depositCents) || 0;
  const priced = unit > 0;
  const qtyChanged = collected && qty !== orderedQty;
  // Her deposit counts on a line he can still buy or has bought — not on one he did not find.
  const hasDeposit = deposit > 0 && live && line?.outcome !== 'out_of_stock';
  const pays = priced && hasDeposit;
  const priceText = priced ? pounds(unit) : DASH;
  const totalText = priced ? pounds(total) : DASH;
  return {
    qtyText: qtyChanged ? t('collector.figures.foundOf', { found: qty, qty: orderedQty }) : String(qty),
    qtyWord: countWord('pieces', orderedQty, t),
    qtyPhrase: qtyChanged ? t('collector.figures.foundOf', { found: qty, qty: pieces(orderedQty) }) : pieces(qty),
    qtyChanged,
    priceLabel: 'collector.figures.unitPrice',
    priceText,
    priceChanged: collected && priced && unit !== listUnit,
    amountLabel: pays ? 'collector.figures.pay' : 'collector.figures.total',
    // the ONE rule for what he pays lives in collector/moneyView.js
    amountText: pays ? pounds(collected ? linePaidCents(line) : linePayableCents(line)) : totalText,
    totalText,
    pays,
    // a deposit is said even when the line has no price yet
    footText: hasDeposit ? t('collector.figures.depositFoot', { total: totalText, deposit: pounds(deposit) }) : null,
  };
}

function groupView(lines) {
  const money = groupMoney(lines);
  const mixed = money.unitCents === null;
  const priced = mixed || money.unitCents > 0;
  const pays = priced && money.depositCents > 0;
  const amountText = priced ? pounds(money.payableCents) : DASH;
  return {
    qtyText: String(money.qty),
    qtyWord: countWord('pieces', money.qty, t),
    qtyPhrase: pieces(money.qty),
    qtyChanged: false,
    priceLabel: mixed ? 'collector.figures.mixedPrices' : 'collector.figures.unitPrice',
    priceText: !mixed && priced ? pounds(money.unitCents) : DASH,
    priceChanged: false,
    amountLabel: 'collector.figures.groupTotal',
    amountText,
    totalText: amountText,
    pays,
    // a deposit is said even when the product has no price yet, as on a single line
    footText: money.depositCents > 0 ? t('collector.figures.groupDepositFoot', { deposit: pounds(money.depositCents) }) : null,
  };
}

const view = computed(() => (props.lines ? groupView(props.lines) : lineView(props.line)));

/**
 * «3 قطع × 135 = 405 ج.م», and «· تدفع 205» after it when she paid a deposit;
 * a grouped card says «· بعد عربون 200 مدفوع في المحل» after its sum.
 */
const sentence = computed(() => {
  const { qtyPhrase, priceText, totalText, amountText, pays, footText } = view.value;
  const sum = t('collector.line.qtyPrice', { qty: qtyPhrase, price: priceText, total: totalText });
  if (props.lines) return footText ? `${sum} · ${footText}` : sum;
  return pays ? `${sum} · ${t('collector.figures.pay')} ${amountText}` : sum;
});
</script>

<style scoped>
.c-figures {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.c-fig {
  display: flex;
  align-items: stretch;
  gap: 6px;
}

/* A cell is never narrower than its number: the number does not break, and on
   a narrow phone the small unit drops under it before anything spills out of
   the card. */
.c-fig__cell {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  box-sizing: border-box;
  padding: 7px 10px 8px;
  border-radius: 12px;
  /* a surface that is itself the ground (the line sheet) sets --c-fig-plain */
  background: var(--c-fig-plain, var(--c-ground));
  color: var(--c-ink);
}

.c-fig__cell--total { flex-grow: 1.3; }

.c-fig__cell--qty {
  /* 78 px, growing only for what cannot fit in it («2 من 3», «12 قطعة») */
  flex: 0 0 auto;
  min-width: 78px;
  background: var(--c-indigo);
  color: #ffffff;
}

.c-fig__lab {
  font-size: 11px;
  font-weight: 600;
  color: var(--c-muted);
}

.c-fig__val {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 4px;
  font-size: 21px;
  font-weight: 700;
  line-height: 1.15;
}

.c-fig__num { white-space: nowrap; }

.c-fig__unit {
  font-size: 11px;
  font-weight: 500;
  color: var(--c-muted);
}

.c-fig__val--long { font-size: 18px; }

.c-fig__cell--qty .c-fig__val { font-size: 28px; }
.c-fig__cell--qty .c-fig__val--long { font-size: 18px; line-height: 1.5; }

.c-fig__cell--qty .c-fig__lab,
.c-fig__cell--qty .c-fig__unit { color: var(--c-head-sub); }

/* What he pays after her deposit. */
.c-fig__cell--pay { background: var(--c-blue-fill); }

.c-fig__cell--pay .c-fig__lab,
.c-fig__cell--pay .c-fig__val,
.c-fig__cell--pay .c-fig__unit { color: var(--c-blue-text); }

/* A figure that differs from the order. */
.c-fig__cell--chg { background: var(--c-amber-fill); }

.c-fig__cell--chg .c-fig__lab,
.c-fig__cell--chg .c-fig__val,
.c-fig__cell--chg .c-fig__unit { color: var(--c-amber-text); }

.c-fig__op {
  align-self: center;
  font-size: 16px;
  font-weight: 700;
  color: var(--c-muted);
}

.c-fig__foot {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--c-blue-text);
}

/* A 360 px phone: «2,240 ج.م» is 1 px wider than its cell at the 6 px gap, and
   the unit would drop under the number. */
@media (max-width: 374px) {
  .c-fig { gap: 4px; }
}
</style>
